import axios from 'axios'
import { APIMetrics, APIKey, APIEndpoint, OAuthClient, Webhook } from '@/types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.yourdomain.com'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add API key to requests if available
api.interceptors.request.use((config) => {
  const apiKey = localStorage.getItem('api_key')
  if (apiKey) {
    config.headers['X-API-Key'] = apiKey
  }
  return config
})

// API Metrics
export const fetchMetrics = async (): Promise<APIMetrics> => {
  const response = await api.get('/v1/metrics')
  return response.data
}

// API Keys Management
export const fetchAPIKeys = async (): Promise<APIKey[]> => {
  const response = await api.get('/v1/api-keys')
  return response.data.data
}

export const createAPIKey = async (data: { name: string; permissions: string[]; tier: string }): Promise<APIKey> => {
  const response = await api.post('/v1/api-keys', data)
  return response.data
}

export const deleteAPIKey = async (keyId: string): Promise<void> => {
  await api.delete(`/v1/api-keys/${keyId}`)
}

// API Endpoints
export const fetchAPIEndpoints = async (): Promise<APIEndpoint[]> => {
  const response = await api.get('/v1/openapi.json')
  // Transform OpenAPI spec to our endpoint format
  const spec = response.data
  const endpoints: APIEndpoint[] = []
  
  Object.entries(spec.paths || {}).forEach(([path, methods]: [string, any]) => {
    Object.entries(methods).forEach(([method, details]: [string, any]) => {
      endpoints.push({
        id: `${method.toUpperCase()}_${path}`,
        path,
        method: method.toUpperCase() as any,
        description: details.summary || details.description || '',
        parameters: details.parameters || [],
        responses: Object.entries(details.responses || {}).map(([status, response]: [string, any]) => ({
          status: parseInt(status),
          description: response.description || '',
          schema: response.schema
        })),
        authentication: !!(details.security && details.security.length > 0)
      })
    })
  })
  
  return endpoints
}

export const testAPIEndpoint = async (endpoint: APIEndpoint, params: any): Promise<any> => {
  const config: any = {
    method: endpoint.method.toLowerCase(),
    url: endpoint.path,
  }
  
  if (endpoint.method === 'POST' || endpoint.method === 'PUT') {
    config.data = params.body
  } else {
    config.params = params.query
  }
  
  const response = await api(config)
  return response.data
}

// OAuth Clients
export const fetchOAuthClients = async (): Promise<OAuthClient[]> => {
  const response = await api.get('/oauth/clients')
  return response.data.data
}

export const createOAuthClient = async (data: {
  name: string
  description: string
  redirect_uris: string[]
  scopes: string[]
}): Promise<OAuthClient> => {
  const response = await api.post('/oauth/clients', data)
  return response.data
}

// Webhooks
export const fetchWebhooks = async (): Promise<Webhook[]> => {
  const response = await api.get('/webhooks')
  return response.data.data
}

export const createWebhook = async (data: {
  url: string
  events: string[]
  secret?: string
}): Promise<Webhook> => {
  const response = await api.post('/webhooks', data)
  return response.data
}

export const updateWebhook = async (id: string, data: Partial<Webhook>): Promise<Webhook> => {
  const response = await api.put(`/webhooks/${id}`, data)
  return response.data
}

export const deleteWebhook = async (id: string): Promise<void> => {
  await api.delete(`/webhooks/${id}`)
}
